import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityViewSwitcher } from '../components/activity/ActivityViewSwitcher';
import { ActivityMonthView } from '../components/activity/ActivityMonthView';
import { ActivityWeekView } from '../components/activity/ActivityWeekView';
import { ActivityDayView } from '../components/activity/ActivityDayView';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { MaintenanceBanner } from '../components/MaintenanceBanner';
import { LowCreditsBanner } from '../components/LowCreditsBanner';
import { Sidebar } from '../components/Sidebar';
import { MobileNav } from '../components/MobileNav';
import { PageHeader } from '../components/design-system/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Menu, Settings, LogOut } from 'lucide-react';

type ViewType = 'day' | 'week' | 'month';

interface ActivityItem {
  id: string;
  type: 'blog' | 'campaign' | 'social';
  title: string;
  date: Date;
  status: string;
  platform?: string;
  imageUrl?: string;
}

export function Activiteit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewType>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadActivities();
  }, [user, navigate]);

  const loadActivities = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);

      const [blogsResult, campaignsResult, socialsResult] = await Promise.all([
        supabase
          .from('published_blogs')
          .select('id, title, published_at, status, featured_image_url')
          .eq('user_id', user.id)
          .gte('published_at', startOfMonth.toISOString())
          .lte('published_at', endOfMonth.toISOString())
          .order('published_at', { ascending: false }),

        supabase
          .from('email_campaigns')
          .select('id, subject, scheduled_at, sent_at, status, blog_id, published_blogs(featured_image_url)')
          .eq('user_id', user.id)
          .or(`scheduled_at.gte.${startOfMonth.toISOString()},sent_at.gte.${startOfMonth.toISOString()}`)
          .order('scheduled_at', { ascending: false }),

        supabase
          .from('social_media_posts')
          .select('id, post_content, platform, scheduled_at, published_at, status, image_url')
          .eq('user_id', user.id)
          .or(`scheduled_at.gte.${startOfMonth.toISOString()},published_at.gte.${startOfMonth.toISOString()}`)
          .order('scheduled_at', { ascending: false })
      ]);

      const allItems: ActivityItem[] = [];

      if (blogsResult.data) {
        blogsResult.data.forEach(blog => {
          if (blog.published_at) {
            allItems.push({
              id: blog.id,
              type: 'blog',
              title: blog.title,
              date: new Date(blog.published_at),
              status: blog.status || 'published',
              imageUrl: blog.featured_image_url || undefined
            });
          }
        });
      }

      if (campaignsResult.data) {
        campaignsResult.data.forEach((campaign: any) => {
          const date = campaign.sent_at || campaign.scheduled_at;
          if (date) {
            allItems.push({
              id: campaign.id,
              type: 'campaign',
              title: campaign.subject,
              date: new Date(date),
              status: campaign.status || 'draft',
              imageUrl: campaign.published_blogs?.featured_image_url || undefined
            });
          }
        });
      }

      if (socialsResult.data) {
        socialsResult.data.forEach(social => {
          const date = social.published_at || social.scheduled_at;
          if (date) {
            allItems.push({
              id: social.id,
              type: 'social',
              title: social.post_content.substring(0, 80) + (social.post_content.length > 80 ? '...' : ''),
              date: new Date(date),
              status: social.status,
              platform: social.platform,
              imageUrl: social.image_url || undefined
            });
          }
        });
      }

      setItems(allItems);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadActivities();
    }
  }, [currentDate, user]);

  const handleDayClick = (date: Date) => {
    setCurrentDate(date);
    setView('day');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <Sidebar onSignOut={handleSignOut} />
        <MobileNav
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          onSignOut={handleSignOut}
        />
        <div className="lg:pl-64">
          <MaintenanceBanner />
          <LowCreditsBanner />
          <header className="bg-white border-b border-gray-200 sticky top-0 z-40 lg:hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Menu"
                  >
                    <Menu className="w-6 h-6" />
                  </button>
                  <img
                    src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
                    alt="Dibbi Logo"
                    className="h-8 lg:hidden"
                  />
                </div>
                <div className="flex items-center gap-3 lg:hidden">
                  <button
                    onClick={() => navigate('/dashboard/settings')}
                    className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Instellingen"
                  >
                    <Settings className="w-6 h-6" />
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Uitloggen"
                  >
                    <LogOut className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </header>
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pt-8">
            <LoadingSpinner text="Activiteiten laden..." />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <Sidebar onSignOut={handleSignOut} />
      <MobileNav
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onSignOut={handleSignOut}
      />
      <div className="lg:pl-64">
        <MaintenanceBanner />
        <LowCreditsBanner />
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40 lg:hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
                <img
                  src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
                  alt="Dibbi Logo"
                  className="h-8 lg:hidden"
                />
              </div>
              <div className="flex items-center gap-3 lg:hidden">
                <button
                  onClick={() => navigate('/dashboard/settings')}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Instellingen"
                >
                  <Settings className="w-6 h-6" />
                </button>
                <button
                  onClick={handleSignOut}
                  className="p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                  title="Uitloggen"
                >
                  <LogOut className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pt-8">
          <div className="space-y-6">
            <PageHeader
              title="Activiteit"
              subtitle="Overzicht van al jouw blogs, campagnes en social media posts"
            />

            <ActivityViewSwitcher
              view={view}
              onViewChange={setView}
              currentDate={currentDate}
              onDateChange={setCurrentDate}
            />

            {view === 'month' && (
              <ActivityMonthView
                currentDate={currentDate}
                items={items}
                onDayClick={handleDayClick}
              />
            )}

            {view === 'week' && (
              <ActivityWeekView
                currentDate={currentDate}
                items={items}
              />
            )}

            {view === 'day' && (
              <ActivityDayView
                currentDate={currentDate}
                items={items}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
